import { isEmpty } from 'lodash';
import { useContext } from 'react';
import CheckTodoIcon from '@shared/assets/icons/v2/check-todo.svg';
import { MBButton } from '@shared/components/MBButton';
import { View } from '@shared/components/View';
import { MBEmptyListPlaceholder, SelectableRadioDot } from '@shared/components/list';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBSearchBar } from '@shared/components/searchBar';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody1, MBTextOverlineBold } from '@shared/components/typography';
import { useWorkflowDispatch } from '@shared/scenes/workflows/context/context';
import { useSubforms } from '@shared/scenes/workflows/subforms/useSubforms';
import { ColorThemeContext, Colors, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { useDebouncedState } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';

interface Props {
  workflowTemplateId: string;
  component: API.SubformComponent;
}

export function DefaultSubformSelection({ workflowTemplateId, component }: Props) {
  const colors = useContext(ColorThemeContext);
  const [query, setQuery] = useDebouncedState('', 300);
  const { subforms, loading, hasMore, loadMore } = useSubforms({ workflowTemplateId, name: query });
  const dispatch = useWorkflowDispatch();

  const onPress = (newWorkflowTemplateId: string) => {
    const newDefault =
      component.default?.workflowTemplateId === newWorkflowTemplateId ? '' : newWorkflowTemplateId;
    dispatch?.({
      type: 'updateComponent',
      args: {
        ...component,
        default: isEmpty(newDefault) ? null : { workflowTemplateId: newDefault },
      } as API.SubformComponent,
    });
  };

  return (
    <View paddingBottom={2}>
      <View paddingHorizontal={3} paddingVertical={2}>
        <MBTextOverlineBold color={colors.foregroundInactive}>
          {i18n('workflowTemplate.responseOptions')}
        </MBTextOverlineBold>
      </View>
      <View paddingVertical={1} paddingHorizontal={1}>
        <MBSearchBar
          value={query}
          onChangeText={setQuery}
          placeholder={i18n('workflowTemplate.subformsSearch')}
        />
      </View>
      {loading && (
        <View center style={{ height: 150 }}>
          <MBLoadingIndicator />
        </View>
      )}
      {!loading && isEmpty(subforms) && (
        <MBEmptyListPlaceholder
          title={i18n('workflowTemplate.subformsNoMatchTitle')}
          description={i18n('workflowTemplate.subformsNoMatchDescription')}
          image={CheckTodoIcon}
          imageSize={grid(9)}
        />
      )}
      {!loading &&
        subforms?.map(form => (
          <DefaultSubformSelectionRow
            key={form.workflowTemplate?.id}
            name={form.workflowTemplate?.name}
            isSelected={form.workflowTemplate?.id === component.default?.workflowTemplateId}
            onPress={() => onPress(form.workflowTemplate?.id)}
          />
        ))}
      {hasMore && (
        <View paddingVertical={3}>
          <MBButton
            title={i18n('card.list.showMore')}
            size="small"
            theme="transparent"
            bold={false}
            borderColor={Colors.v2.gray10}
            onPress={loadMore}
            style={{ alignSelf: 'center' }}
          />
        </View>
      )}
    </View>
  );
}

interface DefaultSubformSelectionRowProps {
  name: string;
  isSelected: boolean;
  onPress();
}

function DefaultSubformSelectionRow({
  name,
  isSelected,
  onPress,
}: DefaultSubformSelectionRowProps) {
  return (
    <MBTouchableOpacity onPress={onPress}>
      <View flex row centerVertically paddingVertical={0.25} paddingHorizontal={3}>
        <View wrap flex={4} style={{ alignContent: 'flex-start' }}>
          <MBTextBody1>{name}</MBTextBody1>
        </View>
        <View flex style={{ alignItems: 'flex-end' }}>
          <SelectableRadioDot selected={isSelected} radio={true} />
        </View>
      </View>
    </MBTouchableOpacity>
  );
}
